












































import { computed, defineComponent, ref, PropType } from 'vue';
import { useDatePicker } from '../../composables/useDatePicker';
import type {
  DatePickerProps,
  DatePickerEmits,
  DatePickerType,
  DatePickerSize,
  DatePickerValue,
  DatePickerRef,
} from '../../types/datePicker.types';

export default defineComponent({
  name: 'DatePicker',
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    modelValue: {
      type: [String, Date, Array] as PropType<DatePickerValue>,
      default: null,
    },
    type: {
      type: String as PropType<DatePickerType>,
      default: 'date' as DatePickerType,
    },
    format: {
      type: String,
      default: undefined,
    },
    valueFormat: {
      type: String,
      default: undefined,
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String as PropType<DatePickerSize>,
      default: 'default' as DatePickerSize,
    },
    pickerOptions: {
      type: Object as PropType<Record<string, any>>,
      default: undefined,
    },
    rangeSeparator: {
      type: String,
      default: ' - ',
    },
    startPlaceholder: {
      type: String,
      default: '',
    },
    endPlaceholder: {
      type: String,
      default: '',
    },
    defaultTime: {
      type: [String, Array] as PropType<string | string[]>,
      default: undefined,
    },
    prefixIcon: {
      type: String,
      default: undefined,
    },
    clearIcon: {
      type: String,
      default: undefined,
    },
    validateEvent: {
      type: Boolean,
      default: true,
    },
    name: {
      type: String,
      default: undefined,
    },
    id: {
      type: String,
      default: undefined,
    },
    ariaLabel: {
      type: String,
      default: undefined,
    },
    popperClass: {
      type: String,
      default: undefined,
    },
    popperAppendToBody: {
      type: Boolean,
      default: true,
    },
    teleported: {
      type: Boolean,
      default: true,
    },
    // Custom props
    useDefaultFormat: {
      type: Boolean,
      default: true,
    },
    useDefaultValueFormat: {
      type: Boolean,
      default: true,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    errorClass: {
      type: String,
      default: 'red-border',
    },
    minDate: {
      type: [String, Date] as PropType<string | Date>,
      default: undefined,
    },
    maxDate: {
      type: [String, Date] as PropType<string | Date>,
      default: undefined,
    },
    disabledDates: {
      type: Function as PropType<(date: Date) => boolean>,
      default: undefined,
    },
    firstDayOfWeek: {
      type: Number,
      default: 1,
    },
  },
  emits: ['update:modelValue', 'change', 'blur', 'focus', 'calendar-change', 'visible-change', 'panel-change'],
  setup(props: DatePickerProps, { emit }: { emit: DatePickerEmits }) {
    const datePicker = ref<DatePickerRef>(null);

    // Use the date picker composable for common functionality
    const {
      computedFormat,
      computedValueFormat,
      computedPickerOptions,
      handleChange,
      handleBlur,
      handleFocus,
      handleCalendarChange,
      handleVisibleChange,
      handlePanelChange,
    } = useDatePicker(props, emit);

    // Internal value for v-model
    const internalValue = computed({
      get: () => props.modelValue,
      set: value => emit('update:modelValue', value),
    });

    // Expose methods for parent components
    const focus = () => {
      if (datePicker.value) {
        datePicker.value.focus();
      }
    };

    const blur = () => {
      if (datePicker.value) {
        datePicker.value.blur();
      }
    };

    const clear = () => {
      if (datePicker.value) {
        datePicker.value.clear();
      }
    };

    return {
      datePicker,
      internalValue,
      computedFormat,
      computedValueFormat,
      computedPickerOptions,
      handleChange,
      handleBlur,
      handleFocus,
      handleCalendarChange,
      handleVisibleChange,
      handlePanelChange,
      focus,
      blur,
      clear,
    };
  },
});
